// Ground decals (§11): skid marks, scorch, splats, expanding shockwave rings, and the immediate-mode
// telegraphs (teeter edge glow, ledge stomp ring, item and pebble telegraphs, push guide) that are
// rebuilt every frame from state. One instanced draw call; flat quads on the ground plane that use
// the shared toon sprite atlas, so every decal keeps its ink outline on light and dark floors.
import {
  Color, DoubleSide, DynamicDrawUsage, InstancedBufferAttribute, InstancedBufferGeometry, Mesh, PlaneGeometry,
  ShaderMaterial, type Texture, Vector2,
} from 'three';
import { TUNE } from '@shared/constants';
import { ATLAS, type Cell } from './atlas';
import { INK_HEX, SPRITE_FRAGMENT } from './Particles';

const DECAL_VERTEX = /* glsl */ `
attribute vec3 iPos;
attribute vec4 iShape;   // width, length, rotation, unused
attribute vec4 iColor;
attribute vec2 iCell;
uniform vec2 uGrid;
// Floor gradient (height per metre along world x and z) of a tilted map surface (the Teacup saucer):
// decals on the floor lie on the slope; on the void plane (uKillZ) they stay flat.
uniform vec2 uSlope;
uniform float uKillZ;
varying vec2 vUv;
varying vec4 vColor;
void main() {
  vec2 p = vec2(position.x * iShape.x, position.y * iShape.y);
  float cs = cos(iShape.z);
  float sn = sin(iShape.z);
  p = vec2(p.x * cs - p.y * sn, p.x * sn + p.y * cs);
  float onFloor = clamp(1.0 - iPos.y / uKillZ, 0.0, 1.0);
  vec3 world = iPos + vec3(p.x, dot(uSlope, p) * onFloor, p.y);
  gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
  float col = mod(iCell.x, uGrid.x);
  float row = floor(iCell.x / uGrid.x);
  vUv = vec2((col + uv.x) / uGrid.x, 1.0 - (row + 1.0 - uv.y) / uGrid.y);
  vColor = iColor;
}`;

/** How a timed decal's size and alpha evolve. */
export const DecalAnim = { FADE: 0, EXPAND: 1, POP: 2 } as const;
export type DecalAnim = (typeof DecalAnim)[keyof typeof DecalAnim];

/** A reusable decal description. Positions are sim (x, y) plus the ground height `h`. */
export class DecalSpec {
  x = 0; y = 0; h = 0;
  /** Direction the cell's "up" points along, in sim radians (0 = +x). */
  angle = Math.PI / 2;
  width = 1;
  length = 1;
  /** End scale multiplier (EXPAND / POP). */
  scaleEnd = 1;
  life = 1;
  /** Fraction of life after which the decal fades. */
  fadeAt = 0.7;
  anim: DecalAnim = DecalAnim.FADE;
  r = 1; g = 1; b = 1;
  alpha = 1;
  cell: Cell = 0 as Cell;

  reset(): this {
    this.x = this.y = this.h = 0;
    this.angle = Math.PI / 2;
    this.width = this.length = 1;
    this.scaleEnd = 1;
    this.life = 1;
    this.fadeAt = 0.7;
    this.anim = DecalAnim.FADE;
    this.r = this.g = this.b = 1;
    this.alpha = 1;
    this.cell = 0 as Cell;
    return this;
  }

  color(c: Color): this {
    this.r = c.r;
    this.g = c.g;
    this.b = c.b;
    return this;
  }
}

/** Ground lift that keeps decals above the floor without z-fighting. */
const LIFT = 0.025;
const STRIDE = 16;

export class DecalSystem {
  readonly mesh: Mesh;
  readonly spec = new DecalSpec();
  private cap: number;
  private timed = 0;
  private immediate = 0;
  /** Timed: x, h, y, angle, width, length, scaleEnd, age, life, fadeAt, anim, r, g, b, alpha, cell. */
  private data: Float32Array;
  private readonly geo: InstancedBufferGeometry;
  private aPos!: InstancedBufferAttribute;
  private aShape!: InstancedBufferAttribute;
  private aColor!: InstancedBufferAttribute;
  private aCell!: InstancedBufferAttribute;

  constructor(atlas: Texture, cap: number) {
    this.cap = cap;
    this.data = new Float32Array(cap * STRIDE);
    const geo = new InstancedBufferGeometry();
    const quad = new PlaneGeometry(1, 1);
    geo.index = quad.index;
    geo.setAttribute('position', quad.getAttribute('position'));
    geo.setAttribute('uv', quad.getAttribute('uv'));
    this.geo = geo;
    this.allocate(cap);
    const mat = new ShaderMaterial({
      uniforms: {
        uAtlas: { value: atlas }, uGrid: { value: [ATLAS.cols, ATLAS.rows] }, uInk: { value: new Color(INK_HEX) }, uAdditive: { value: 0 }, uBoost: { value: 1 },
        uSlope: { value: new Vector2() }, uKillZ: { value: TUNE.edge.killZ },
      },
      vertexShader: DECAL_VERTEX,
      fragmentShader: SPRITE_FRAGMENT,
      transparent: true,
      depthWrite: false,
      side: DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -3,
      polygonOffsetUnits: -3,
    });
    this.mesh = new Mesh(geo, mat);
    this.mesh.name = 'decals';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
  }

  get count(): number {
    return this.timed;
  }

  /** Tilts floor decals with a sloped map surface (height per metre along sim x and y); 0, 0 = flat. */
  setSlope(gx: number, gy: number): void {
    const u = (this.mesh.material as ShaderMaterial).uniforms.uSlope.value as Vector2;
    u.set(gx, gy);
  }

  /** Adds a timed decal from `s`. */
  add(s: DecalSpec = this.spec): void {
    let i = this.timed;
    if (i >= this.cap) {
      // Recycle the oldest (lowest index: decals are appended in time order and compacted stably).
      this.data.copyWithin(0, STRIDE, this.cap * STRIDE);
      i = this.cap - 1;
    } else {
      this.timed++;
    }
    const d = this.data;
    const o = i * STRIDE;
    d[o] = s.x;
    d[o + 1] = s.h + LIFT;
    d[o + 2] = s.y;
    d[o + 3] = s.angle - Math.PI / 2;
    d[o + 4] = s.width;
    d[o + 5] = s.length;
    d[o + 6] = s.scaleEnd;
    d[o + 7] = 0;
    d[o + 8] = Math.max(0.016, s.life);
    d[o + 9] = s.fadeAt;
    d[o + 10] = s.anim;
    d[o + 11] = s.r;
    d[o + 12] = s.g;
    d[o + 13] = s.b;
    d[o + 14] = s.alpha;
    d[o + 15] = s.cell;
  }

  /** Starts a frame: ages the timed decals; immediate decals drawn after this live one frame. */
  begin(dt: number): void {
    const d = this.data;
    let w = 0;
    for (let i = 0; i < this.timed; i++) {
      const o = i * STRIDE;
      d[o + 7] += dt;
      if (d[o + 7] >= d[o + 8]) continue;
      if (w !== i) d.copyWithin(w * STRIDE, o, o + STRIDE);
      w++;
    }
    this.timed = w;
    this.immediate = 0;
    const ap = this.aPos.array as Float32Array;
    const as = this.aShape.array as Float32Array;
    const ac = this.aColor.array as Float32Array;
    const ak = this.aCell.array as Float32Array;
    for (let i = 0; i < w; i++) {
      const o = i * STRIDE;
      const f = d[o + 7] / d[o + 8];
      const anim = d[o + 10];
      let k = 1;
      if (anim === DecalAnim.EXPAND) k = 1 + (d[o + 6] - 1) * (1 - (1 - f) * (1 - f) * (1 - f));
      else if (anim === DecalAnim.POP) k = f < 0.2 ? (f / 0.2) * 1.15 : 1.15 + (d[o + 6] - 1.15) * ((f - 0.2) / 0.8);
      const fadeAt = d[o + 9];
      const fade = f < fadeAt ? 1 : 1 - (f - fadeAt) / Math.max(0.001, 1 - fadeAt);
      this.write(i, ap, as, ac, ak, d[o], d[o + 1], d[o + 2], d[o + 3], d[o + 4] * k, d[o + 5] * k, d[o + 11], d[o + 12], d[o + 13], d[o + 14] * fade, d[o + 15]);
    }
  }

  /** A decal for this frame only (telegraphs). `angle` is the sim direction of the cell's up. */
  draw(s: DecalSpec = this.spec): void {
    const i = this.timed + this.immediate;
    if (i >= this.cap + IMMEDIATE_EXTRA) return;
    this.immediate++;
    this.write(i, this.aPos.array as Float32Array, this.aShape.array as Float32Array, this.aColor.array as Float32Array, this.aCell.array as Float32Array,
      s.x, s.h + LIFT, s.y, s.angle - Math.PI / 2, s.width, s.length, s.r, s.g, s.b, s.alpha, s.cell);
  }

  /** Uploads this frame's decals. */
  end(): void {
    const n = this.timed + this.immediate;
    this.geo.instanceCount = n;
    if (n === 0) return;
    for (const a of this.attrs) {
      a.clearUpdateRanges();
      a.addUpdateRange(0, n * a.itemSize);
      a.needsUpdate = true;
    }
  }

  clear(): void {
    this.timed = 0;
    this.immediate = 0;
    this.geo.instanceCount = 0;
  }

  setCapacity(cap: number): void {
    if (cap === this.cap) return;
    this.cap = cap;
    this.data = new Float32Array(cap * STRIDE);
    this.timed = 0;
    this.allocate(cap);
  }

  private attrs: InstancedBufferAttribute[] = [];

  private allocate(cap: number): void {
    const total = cap + IMMEDIATE_EXTRA;
    const attr = (n: number): InstancedBufferAttribute => {
      const a = new InstancedBufferAttribute(new Float32Array(total * n), n);
      a.setUsage(DynamicDrawUsage);
      return a;
    };
    this.aPos = attr(3);
    this.aShape = attr(4);
    this.aColor = attr(4);
    this.aCell = attr(2);
    this.geo.setAttribute('iPos', this.aPos);
    this.geo.setAttribute('iShape', this.aShape);
    this.geo.setAttribute('iColor', this.aColor);
    this.geo.setAttribute('iCell', this.aCell);
    this.attrs = [this.aPos, this.aShape, this.aColor, this.aCell];
    this.geo.instanceCount = 0;
  }

  private write(i: number, ap: Float32Array, as: Float32Array, ac: Float32Array, ak: Float32Array,
    x: number, y: number, z: number, rot: number, w: number, l: number, r: number, g: number, b: number, a: number, cell: number): void {
    const i2 = i * 2;
    const i3 = i * 3;
    const i4 = i * 4;
    ap[i3] = x;
    ap[i3 + 1] = y;
    ap[i3 + 2] = z;
    as[i4] = w;
    as[i4 + 1] = l;
    as[i4 + 2] = rot;
    as[i4 + 3] = 0;
    ac[i4] = r;
    ac[i4 + 1] = g;
    ac[i4 + 2] = b;
    ac[i4 + 3] = a;
    ak[i2] = cell;
    ak[i2 + 1] = 1;
  }
}

/** Room for this many immediate decals beyond the timed capacity. */
const IMMEDIATE_EXTRA = 96;
